import React, { useLayoutEffect, useRef, useState } from "react"
import dayjs from "dayjs"
import relativeTime from "dayjs/plugin/relativeTime"

import {
  getActionsRelatedToRequests,
  getInstancesRelatedToRequests,
  getLatestAction,
  timestampToDateString,
} from "../../../utils"
import { PpeRecordSk, PpeRequest, PpeStatusType, ColumnConfig } from "../../../types"
import { PpeActionRecordSchema, PpeInstanceRecordSchema } from "../../../models/ppe"
import { PpeActions, PpeStatus } from "../../../enums"
import { Table } from "simplygrow-ui"
import { TableService } from "../../../services"
import { TABLE_ROW_HEIGHT } from "../../../constants/table"

dayjs.extend(relativeTime)

type DataRow = {
  requestId: string
  requestDate: string
  latestAction: PpeActionRecordSchema
  numberOfRelatedInstances: number
  numberOfRelatedActions: number
}

export type PpeRequestListingData = {
  actions: Record<PpeRecordSk, PpeActionRecordSchema>
  instances: Record<PpeRecordSk, PpeInstanceRecordSchema>
  requests: Record<PpeRecordSk, PpeRequest>
}

interface PpeRequestListingProps {
  data: PpeRequestListingData
  selectedStatus: PpeStatusType
  onRowClick: (requestId: string) => void
  style?: React.CSSProperties
}

const tableService = new TableService()

const PpeRequestListing = (props: PpeRequestListingProps) => {
  const tableRef = useRef<HTMLDivElement>(null)
  const [tableHeight, setTableHeight] = useState<number>(500)

  useLayoutEffect(() => {
    if (tableRef.current) {
      const tableTopY = tableRef.current.getBoundingClientRect().top
      const browserWindowHeight = window.innerHeight
      setTableHeight(browserWindowHeight - tableTopY)
    }
  }, [tableRef.current])

  const getTableColumnConfigBySelectedStatus = (): ColumnConfig[] => {
    let tableConfig: ColumnConfig[] = []

    if (props.selectedStatus === PpeStatus.READY_FOR_ISSUE) {
      tableConfig = [
        { key: "requestDate", label: "Request date", sizeFactor: 1 },
        { key: "requestedBy", label: "Requested by", sizeFactor: 1 },
        { key: "numberOfItems", label: "Number of items", sizeFactor: 1 },
      ]
    }

    if (props.selectedStatus === PpeStatus.ISSUING) {
      tableConfig = [
        { key: "requestDate", label: "Request date", sizeFactor: 1 },
        { key: "requestedBy", label: "Requested by", sizeFactor: 1 },
        { key: "issuedTo", label: "Issued to", sizeFactor: 1 },
        { key: "numberOfItems", label: "Number of items", sizeFactor: 1 },
      ]
    }

    if (props.selectedStatus === PpeStatus.RETURN_IN_PROGRESS) {
      tableConfig = [
        { key: "requestDate", label: "Request date", sizeFactor: 1 },
        { key: "returnedBy", label: "Returned by", sizeFactor: 1 },
        { key: "returnedTo", label: "Returned to", sizeFactor: 1 },
        { key: "numberOfItems", label: "Number of items", sizeFactor: 1 },
      ]
    }

    if (props.selectedStatus === PpeStatus.COMPLETE) {
      tableConfig = [
        { key: "requestDate", label: "Request date", sizeFactor: 1 },
        { key: "requestedBy", label: "Requested by", sizeFactor: 1 },
        { key: "numberOfItems", label: "Number of items", sizeFactor: 1 },
      ]
    }

    return tableConfig
  }

  const getDataRowsToDisplay = (): DataRow[] => {
    const dataRows: DataRow[] = []
    const { actions, instances, requests } = props.data
    const requestSks = Object.keys(requests)
    requestSks.forEach((sk) => {
      const requestRelatedActions: Record<PpeRecordSk, PpeActionRecordSchema> = getActionsRelatedToRequests(
        [sk],
        actions,
      )
      const latestAction = getLatestAction(requestRelatedActions)
      const dateAndTimeFormat = "YYYY/MM/DD HH:mm"
      const requestDate = timestampToDateString(requests[sk].dateActioned, dateAndTimeFormat)
      const requestRelatedInstances = getInstancesRelatedToRequests([sk], instances)
      dataRows.push({
        requestId: sk,
        requestDate,
        latestAction,
        numberOfRelatedInstances: Object.keys(requestRelatedInstances).length,
        numberOfRelatedActions: Object.values(requestRelatedActions).filter(
          (action: PpeActionRecordSchema) => action.rawData.ppeAction === PpeActions.INITIATE_RETURN_INSTANCE,
        ).length,
      })
    })
    return dataRows
  }

  const sortByRequestDate = (a: Record<string, any>, b: Record<string, any>) => {
    return dayjs(b.requestDate).unix() - dayjs(a.requestDate).unix()
  }

  const prepareReadyForIssueDataDisplay = (dataRows: DataRow[]) => {
    const displayData: Record<string, any>[] = []
    dataRows.forEach((row: DataRow) => {
      const requestedBy =
        `${row.latestAction.rawData.sourceName} ${row.latestAction.rawData.sourceSurname}`.toUpperCase()

      displayData.push({
        id: row.requestId,
        requestId: row.requestId,
        requestDate: row.requestDate,
        requestedBy,
        numberOfItems: row.numberOfRelatedInstances,
      })
    })
    displayData.sort(sortByRequestDate)
    return displayData
  }

  const prepareIssuingDataDisplay = (dataRows: DataRow[]) => {
    const displayData: Record<string, any>[] = []
    dataRows.forEach((row: DataRow) => {
      const requestedBy =
        `${row.latestAction.rawData.sourceName} ${row.latestAction.rawData.sourceSurname}`.toUpperCase()
      const issuedTo = `${row.latestAction.rawData.targetName} ${row.latestAction.rawData.targetSurname}`.toUpperCase()

      displayData.push({
        id: row.requestId,
        requestId: row.requestId,
        requestDate: row.requestDate,
        requestedBy,
        issuedTo,
        numberOfItems: row.numberOfRelatedInstances,
      })
    })
    displayData.sort(sortByRequestDate)
    return displayData
  }

  const prepareReturnInProgressDataDisplay = (dataRows: DataRow[]) => {
    const displayData: Record<string, any>[] = []
    dataRows.forEach((row: DataRow) => {
      const returnedBy =
        `${row.latestAction.rawData.sourceName} ${row.latestAction.rawData.sourceSurname}`.toUpperCase()
      const returnedTo =
        `${row.latestAction.rawData.targetName} ${row.latestAction.rawData.targetSurname}`.toUpperCase()

      displayData.push({
        id: row.requestId,
        requestId: row.requestId,
        requestDate: row.requestDate,
        returnedBy,
        returnedTo,
        numberOfItems: row.numberOfRelatedActions,
      })
    })
    displayData.sort(sortByRequestDate)
    return displayData
  }

  const prepareCompleteDataForDisplay = (dataRows: DataRow[]) => {
    const displayData: Record<string, any>[] = []
    dataRows.forEach((row: DataRow) => {
      const requestedBy =
        `${row.latestAction.rawData.sourceName} ${row.latestAction.rawData.sourceSurname}`.toUpperCase()

      displayData.push({
        id: row.requestId,
        requestId: row.requestId,
        requestDate: row.requestDate,
        requestedBy,
        numberOfItems: row.numberOfRelatedInstances,
      })
    })
    displayData.sort(sortByRequestDate)
    return displayData
  }

  const prepareDataForDisplay = () => {
    const data = getDataRowsToDisplay()

    if (props.selectedStatus === PpeStatus.READY_FOR_ISSUE) {
      return prepareReadyForIssueDataDisplay(data)
    }
    if (props.selectedStatus === PpeStatus.ISSUING) {
      return prepareIssuingDataDisplay(data)
    }
    if (props.selectedStatus === PpeStatus.RETURN_IN_PROGRESS) {
      return prepareReturnInProgressDataDisplay(data)
    }
    if (props.selectedStatus === PpeStatus.COMPLETE) {
      return prepareCompleteDataForDisplay(data)
    }

    return []
  }

  const handleRowClick = (requestId: string) => {
    props.onRowClick(requestId)
  }

  const tableData = prepareDataForDisplay()
  const tableColumnConfig = getTableColumnConfigBySelectedStatus()

  return (
    <div ref={tableRef}>
      <Table
        rows={tableData}
        columns={tableService.convertPfColumnConfigToGridColDef(tableColumnConfig)}
        tableHeight={tableHeight}
        rowHeight={TABLE_ROW_HEIGHT}
        onRowClick={(rowData: any) => {
          handleRowClick(rowData.id)
        }}
        key={`ppeRequests_${tableData.length}_${props.selectedStatus}`}
      />
    </div>
  )
}

export default PpeRequestListing
